import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLenis } from "lenis/react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Music2 } from "lucide-react";
import { NAV_LINKS, LOGO } from "@/data/content";
import { EASE } from "@/lib/motion";
import { useSound } from "@/context/SoundContext";
import { SoundWave } from "@/components/SoundWave";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { enabled, toggleSound } = useSound();
  const lenis = useLenis();
  const navigate = useNavigate();
  const location = useLocation();

  // Over a dark image hero (Home / About top) → use light text.
  const light = !scrolled && (location.pathname === "/" || location.pathname === "/about");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToId = useCallback(
    (id) => {
      const target = document.getElementById(id);
      if (target && lenis) {
        lenis.scrollTo(target, { offset: -80, duration: 1.4 });
      } else if (target) {
        target.scrollIntoView({ behavior: "smooth" });
      }
    },
    [lenis]
  );

  const handleAnchor = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      scrollToId(id);
    }
  };

  const handleRoute = () => {
    setOpen(false);
    if (lenis) lenis.scrollTo(0, { immediate: true });
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: EASE, delay: 0.2 }}
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled
            ? "bg-ivory/85 backdrop-blur-xl border-b border-stone/60 py-3"
            : "bg-transparent border-b border-transparent py-5"
        }`}
      >
        <nav className="mx-auto max-w-[1600px] px-6 md:px-10 flex items-center justify-between">
          <Link
            to="/"
            onClick={handleRoute}
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
            aria-label="Svara Chakra home"
          >
            <span className="bg-ivory rounded-full p-1.5 shadow-[0_2px_20px_rgba(47,79,70,0.08)]">
              <img
                src={LOGO}
                alt="Svara Chakra"
                className="h-9 w-9 md:h-10 md:w-10 object-contain"
              />
            </span>
            <span className="hidden sm:flex flex-col leading-none">
              <span className={`font-serif text-xl md:text-2xl tracking-tight transition-colors duration-500 ${light ? "text-ivory" : "text-forest"}`}>
                Svara Chakra
              </span>
              <span className="flex items-center gap-2 mt-0.5">
                <span className="kicker text-[0.55rem] text-gold">Wellness</span>
                <SoundWave active={enabled} />
              </span>
            </span>
          </Link>

          {/* Desktop links */}
          <ul className="hidden lg:flex items-center gap-4 xl:gap-6">
            {NAV_LINKS.map((link) =>
              link.type === "route" ? (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    onClick={handleRoute}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                    className={`text-[0.7rem] uppercase tracking-[0.12em] transition-colors duration-300 ${
                      light
                        ? location.pathname === link.to ? "text-gold" : "text-ivory/85 hover:text-gold"
                        : location.pathname === link.to ? "text-forest" : "text-charcoal/80 hover:text-forest"
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ) : (
                <li key={link.label}>
                  <button
                    onClick={() => handleAnchor(link.to)}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                    className={`text-[0.7rem] uppercase tracking-[0.12em] transition-colors duration-300 ${
                      light ? "text-ivory/85 hover:text-gold" : "text-charcoal/80 hover:text-forest"
                    }`}
                  >
                    {link.label}
                  </button>
                </li>
              )
            )}
          </ul>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleSound}
              data-testid="sound-toggle"
              aria-pressed={enabled}
              aria-label={enabled ? "Turn sound off" : "Enter with sound"}
              className={`hidden md:inline-flex items-center gap-2 text-[0.68rem] uppercase tracking-[0.14em] transition-colors duration-300 ${
                light
                  ? enabled ? "text-gold" : "text-ivory/85 hover:text-gold"
                  : enabled ? "text-forest" : "text-charcoal/70 hover:text-forest"
              }`}
            >
              <Music2 size={15} strokeWidth={1.5} className={enabled ? "animate-float-slow" : ""} />
              <span className="hidden lg:inline">{enabled ? "Sound On" : "Enter with Sound"}</span>
            </button>
            <Link
              to="/contact"
              onClick={handleRoute}
              data-testid="nav-cta-consultation"
              className={`hidden md:inline-block text-[0.7rem] uppercase tracking-[0.16em] px-6 py-3 transition-colors duration-500 ${
                light ? "bg-ivory text-forest hover:bg-gold" : "bg-forest text-ivory hover:bg-charcoal"
              }`}
            >
              Book a Consultation
            </Link>
            <button
              onClick={() => setOpen(true)}
              className={`lg:hidden transition-colors duration-500 ${light ? "text-ivory" : "text-forest"}`}
              aria-label="Open menu"
              data-testid="nav-menu-open"
            >
              <Menu size={26} strokeWidth={1.3} />
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-forest text-ivory flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-ivory/15">
              <span className="font-serif text-2xl">Svara Chakra</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="nav-menu-close"
              >
                <X size={28} strokeWidth={1.3} />
              </button>
            </div>
            <ul className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-1">
              {NAV_LINKS.map((link, i) => (
                <motion.li
                  key={link.label}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, ease: EASE }}
                  className="border-b border-ivory/10"
                >
                  {link.type === "route" ? (
                    <Link
                      to={link.to}
                      onClick={handleRoute}
                      className="block py-4 font-serif text-3xl"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleAnchor(link.to)}
                      className="block w-full text-left py-4 font-serif text-3xl"
                    >
                      {link.label}
                    </button>
                  )}
                </motion.li>
              ))}
            </ul>
            <div className="px-6 pb-10 space-y-4">
              <button
                onClick={() => {
                  toggleSound();
                }}
                data-testid="sound-toggle-mobile"
                aria-pressed={enabled}
                className="w-full flex items-center justify-center gap-3 border border-ivory/30 text-ivory uppercase tracking-[0.16em] text-xs py-4"
              >
                <Music2 size={16} strokeWidth={1.5} className={enabled ? "text-gold" : ""} />
                {enabled ? "Sound On" : "Enter with Sound"}
              </button>
              <Link
                to="/contact"
                onClick={handleRoute}
                className="block text-center bg-gold text-forest uppercase tracking-[0.16em] text-sm py-4"
              >
                Book a Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
